import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { feedbackPayArrived, feedbackPaySend } from '../../utils/appFeedback';
import { useAutoCancelDraft } from '../../hooks/useAutoCancelDraft';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";
const TIP_OPTIONS = [8, 15, 25, 50];

export default function DemoGuestPayPage() {
  const { txnId } = useParams();
  const navigate = useNavigate();
  const [txn, setTxn] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [selectedTip, setSelectedTip] = useState(0);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);

  // If the guest side closes/backs out of this page without approving,
  // kill the draft so it doesn't linger as pending for anyone.
  useAutoCancelDraft({ txnId, status: paid ? 'PAID' : txn?.status, kind: 'demo' });

  const fetchTxn = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/demo-transactions/${txnId}`, { headers: { Authorization: `Bearer ${token}` } });
      setTxn(res.data);
      if (res.data.status === 'PAID') setPaid(true);
      if (res.data.status === 'EXPIRED' || res.data.status === 'CANCELLED') {
        navigate('/demo-scan', { replace: true });
      }
    } catch (_) {
      // Silently fall back to the demo home — no 404 toast.
      navigate('/demo-scan', { replace: true });
    }
  }, [txnId, navigate]);

  // Poll so the pay screen reacts in real-time to a venue-side cancel or
  // the 60s auto-expiry — otherwise the guest could still tap Approve on
  // a stale draft and hit a 410 at the end.
  useEffect(() => {
    const iv = window.setInterval(fetchTxn, 2000);
    return () => window.clearInterval(iv);
  }, [fetchTxn]);

  useEffect(() => { fetchTxn(); }, [fetchTxn]);

  const handleApprove = async () => {
    if (!txn) return;
    const tipPct = customTip !== ''
      ? (txn.discounted_amount > 0 ? (parseFloat(customTip) || 0) / txn.discounted_amount * 100 : 0)
      : selectedTip;
    setPaying(true);
    feedbackPaySend();
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/demo-transactions/${txnId}/approve`,
        { tip_percent: tipPct },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTxn(res.data);
      setPaid(true);
      feedbackPayArrived();
      toast.success('Demo payment approved!');
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) {
        // Expired while the guest was deciding — silently exit.
        navigate('/demo-scan', { replace: true });
        return;
      }
      toast.error(err.response?.data?.detail || 'Could not approve');
    } finally {
      setPaying(false);
    }
  };

  if (!txn) {
    return (
      <div className="app-shell" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--surface)' }}>
        <img src="/allure-logo.png" alt="" style={{ width: 80, height: 71 }} className="animate-pulse" />
      </div>
    );
  }

  if (paid) {
    return (
      <div className="app-shell" data-testid="demo-pay-success-page" style={{ backgroundColor: 'var(--surface)', minHeight: '100vh', paddingBottom: 40, position: 'relative' }}>
        <button
          type="button"
          onClick={() => navigate('/demo-scan')}
          aria-label="Close"
          data-testid="demo-pay-success-close"
          style={{ position: 'absolute', top: 'max(14px, env(safe-area-inset-top))', right: 18, width: 40, height: 40, borderRadius: '50%', background: 'var(--surface-2)', border: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}
        >
          <X size={20} color="var(--text)" />
        </button>
        <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 36 }}>
          <RouteTransitionLogo width={120} height={108} dataTestId="demo-pay-success-logo" />
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 20 }}>
            <div style={{ width: 70, height: 70, borderRadius: '50%', backgroundColor: 'var(--status-success-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <Check size={36} color="white" strokeWidth={3} />
            </div>
            <h2 style={{ fontFamily: FONT, fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 10, textAlign: 'center' }}>Demo payment approved!</h2>
            <p style={{ fontFamily: FONT, fontSize: 14, color: 'var(--text-2)', margin: 0, textAlign: 'center', maxWidth: 280 }}>
              The venue sees the result on their screen.
            </p>
            <button onClick={() => navigate('/demo-scan')} className="premium-pill-button" style={{ marginTop: 22, padding: '13px 40px', fontFamily: FONT, fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>
              Back to Demo home
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell" data-testid="demo-pay-page" style={{ backgroundColor: 'var(--surface)', minHeight: '100vh', paddingBottom: 40, position: 'relative' }}>
      <button
        type="button"
        onClick={() => navigate('/demo-scan')}
        aria-label="Close demo"
        data-testid="demo-pay-close"
        style={{ position: 'absolute', top: 'max(14px, env(safe-area-inset-top))', right: 18, width: 40, height: 40, borderRadius: '50%', background: 'var(--surface-2)', border: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}
      >
        <X size={20} color="var(--text)" />
      </button>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 28 }}>
        <RouteTransitionLogo width={120} height={108} dataTestId="demo-pay-logo" />

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 28, marginBottom: 10 }}>
          <span data-testid="demo-pay-original-price" style={{ fontFamily: FONT, fontSize: 28, fontWeight: 700, color: 'var(--text)', textDecoration: 'line-through' }}>
            {txn.original_amount.toFixed(2)}€
          </span>
          <span style={{ fontFamily: FONT, fontSize: 24, color: 'var(--text)' }}>→</span>
          <span data-testid="demo-pay-discounted-price" style={{ fontFamily: FONT, fontSize: 32, fontWeight: 700, color: 'var(--status-success-text)', textDecoration: 'underline', textUnderlineOffset: '4px' }}>
            {(Number(txn.discounted_amount || 0) + Number(txn.guest_flat_fee || 0.39)).toFixed(2)}€
          </span>
        </div>
        <p style={{ fontFamily: FONT, fontSize: 13, color: 'var(--text-2)', textAlign: 'center', margin: '0 0 24px', lineHeight: 1.45, maxWidth: 300 }}>
          {`${Number(txn.discounted_amount || 0).toFixed(2)}€ after ${Math.round(txn.discount_percent)}% discount + ${Number(txn.guest_flat_fee || 0.39).toFixed(2)}€ service fee`}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontFamily: FONT, fontSize: 22, color: 'var(--text)' }}>Optional staff tip</span>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          {TIP_OPTIONS.map((tip) => {
            const isSelected = selectedTip === tip;
            return (
              <button
                key={tip}
                onClick={() => {
                  if (isSelected) { setSelectedTip(0); setCustomTip(''); }
                  else { setSelectedTip(tip); setCustomTip((txn.discounted_amount * tip / 100).toFixed(2)); }
                }}
                data-testid={`demo-pay-tip-${tip}`}
                aria-pressed={isSelected}
                style={{ padding: '8px 14px', borderRadius: 10, fontFamily: FONT, fontSize: 18, color: isSelected ? 'var(--accent-text-soft)' : 'var(--text-3)', background: isSelected ? 'var(--accent-surface)' : 'var(--surface-2)', border: isSelected ? '1px solid var(--accent-border-soft)' : '1px solid var(--card-border)', cursor: 'pointer' }}
              >
                {tip}%
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9]*[.,]?[0-9]*"
            autoComplete="off"
            value={customTip}
            onChange={(e) => { setCustomTip(e.target.value.replace(/[^0-9.,]/g, '')); setSelectedTip(0); }}
            placeholder="0.00"
            data-testid="demo-pay-tip-input"
            style={{ width: 120, height: 40, padding: '0 12px', fontFamily: FONT, fontSize: 20, color: 'var(--text)', textAlign: 'center', backgroundColor: 'var(--surface-2)', border: '1px solid var(--card-border)', borderRadius: 8, outline: 'none' }}
          />
          <span style={{ fontFamily: FONT, fontSize: 22, color: 'var(--text-2)' }}>€</span>
        </div>

        <motion.button
          onClick={handleApprove}
          disabled={paying}
          whileTap={{ scale: 0.98 }}
          data-testid="demo-pay-approve-btn"
          style={{ width: 'auto', minWidth: 240, maxWidth: '90%', padding: '14px 32px', fontFamily: FONT, fontSize: 18, fontWeight: 600, color: '#FFFFFF', background: '#1DB954', border: 'none', borderRadius: 9999, cursor: paying ? 'not-allowed' : 'pointer', opacity: paying ? 0.6 : 1, boxShadow: '0 6px 20px rgba(29,185,84,0.3)', whiteSpace: 'nowrap' }}
        >
          {paying ? 'Approving…' : 'Approve demo payment'}
        </motion.button>

        <button
          onClick={() => navigate('/demo-scan')}
          disabled={paying}
          data-testid="demo-pay-cancel-btn"
          style={{ marginTop: 14, padding: '10px 28px', fontFamily: FONT, fontSize: 16, color: 'var(--text-2)', background: 'transparent', border: 'none', cursor: paying ? 'not-allowed' : 'pointer', opacity: paying ? 0.4 : 1 }}
        >
          Cancel
        </button>

        <p style={{ marginTop: 22, fontFamily: FONT, fontSize: 11, color: 'var(--text-3)', textAlign: 'center' }}>
          Demo mode · No real payment is processed.
        </p>
      </div>
    </div>
  );
}
